"use client";

import cn from "classnames";
import Image from "next/image";
import { useState, useMemo, useEffect } from "react";

import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { Typography, useMediaQuery } from "@mui/material";

import { StyleHooks, ProviderHooks } from "@/src/hook";
import { StrapiType } from "@/src/type";

import { VideoPlayer } from "../VideoPlayer";

import theme from "@/styles/scss/theme.module.scss";
import styles from "./VideoDialog.module.scss";

interface VideoDialogProps {
  id: string;
  lazy: "lazy" | "eager";
  controls?: boolean;
  heroLargeScreen?: StrapiType.StrapiAsset;
  heroSmallScreen?: StrapiType.StrapiAsset;
  heroPortraitSmallScreen?: StrapiType.StrapiAsset;
  openDialog: boolean;
  eventCaptureWall: boolean;
  handleCloseDialog: () => void;
  handleCloseDialogAndSkip?: () => void;
}

function VideoDialog({
  id,
  lazy,
  controls,
  heroLargeScreen,
  heroSmallScreen,
  heroPortraitSmallScreen,
  openDialog,
  eventCaptureWall,
  handleCloseDialog,
  handleCloseDialogAndSkip,
}: VideoDialogProps) {
  const cms = ProviderHooks.useCMS();
  const shared = cms?.webConfig?.shared;
  const isMediumBreakpoint = StyleHooks.useBreakPointDown("md");
  //this determines if the device is oriented as portait
  const isPortrait = useMediaQuery("(orientation: portrait)");
  //this determines if a portrait video has been uploaded
  //a portrait video overrides normal video on mobile
  const isPortraitVideo = useMemo(() => !!heroPortraitSmallScreen?.data && isMediumBreakpoint, [heroPortraitSmallScreen, isMediumBreakpoint]);
  const isLight = isPortraitVideo ? true : !isPortrait || !isMediumBreakpoint;
  const [muted, setMuted] = useState<boolean>(true);
  const [activated, setActivated] = useState<boolean>(true);
  const videoAsset: StrapiType.StrapiAsset | undefined = useMemo(
    () =>
      isMediumBreakpoint && (heroSmallScreen?.data || heroPortraitSmallScreen?.data)
        ? heroPortraitSmallScreen?.data
          ? heroPortraitSmallScreen
          : heroSmallScreen
        : heroLargeScreen,
    [isMediumBreakpoint, heroLargeScreen, heroSmallScreen, heroPortraitSmallScreen]
  );

  useEffect(() => {
    function handleEvent() {
      setMuted(false);
      window.removeEventListener("click", handleEvent);
    }

    window.addEventListener("click", handleEvent, { passive: false });
    return () => {
      window.removeEventListener("click", handleEvent);
    };
  }, []);

  useEffect(() => {
    if (openDialog) {
      setActivated(true);

      const html = document.querySelector("html");
      if (html) {
        html.setAttribute("style", "-webkit-text-size-adjust: 100%;");
      }
    }

    if (!openDialog) {
      setTimeout(() => {
        const html = document.querySelector("html");
        if (html) {
          html.removeAttribute("style");
        }
      }, 4000);
    }
  }, [openDialog]);

  function volumenButton() {
    if (isLight) {
      return muted ? "/image/icon_muted.svg" : "/image/icon_volume.svg";
    } else {
      return muted ? "/image/icon_muted_dark.svg" : "/image/icon_volume_dark.svg";
    }
  }

  function nextIconColor(): string {
    return isLight ? "/image/icon_arrow_down_thick.svg" : "/image/icon_arrow_down_thick_dark.svg";
  }

  const handleMuted = () => {
    setMuted((muted) => !muted);
  };

  const updateOperation = () => {
    if (!activated) {
      return;
    }
    return openDialog ? "begin" : "pause";
  };

  if (!videoAsset?.data) {
    return <></>;
  }

  if (!eventCaptureWall && !openDialog) {
    return <></>;
  }

  return (
    <div className={styles.dialogContainer}>
      <div
        className={styles.dialogExit}
        onClick={handleCloseDialog}
      >
        <CloseIcon
          fontSize="large"
          sx={{
            color: !isPortraitVideo && isPortrait && isMediumBreakpoint ? theme.primaryMain : theme.secondaryMain,
          }}
        />
      </div>
      <div className={styles.dialogInner}>
        <div className={styles.dialogVideoContainer}>
          <VideoPlayer
            lazy={lazy}
            asset={videoAsset}
            controls={controls}
            videoId={`VideoDialog.${id}`}
            classes={{
              container: styles.dialogVideoModule,
              video: cn(styles.dialogVideoModuleFrame, isPortraitVideo ? styles.portrait : undefined),
            }}
            muted={muted}
            updateOperation={updateOperation()}
            AfterVideoComponent={
              <>
                {!isPortraitVideo && (
                  <div className={styles.dialogVideoPortiat}>
                    <Image
                      src={"/image/icon_rotate.svg"}
                      alt={"Rotate your phone for best viewing experience"}
                      width={40}
                      height={40}
                      priority={true}
                    />
                    <Typography fontSize={14}>{shared?.imageTakeoverRotationText ?? "Rotate your phone for optimal viewing"}</Typography>
                  </div>
                )}
              </>
            }
          />
        </div>
        <div className={styles.dialogButtons}>
          <IconButton onClick={handleMuted}>
            <Image
              src={volumenButton()}
              alt={muted ? "Turn Volume On Icon" : "Turn Volume Off Icon"}
              width={40}
              height={40}
              priority={true}
            />
          </IconButton>
          {typeof handleCloseDialogAndSkip === "function" && (
            <IconButton onClick={handleCloseDialogAndSkip}>
              <Image
                src={nextIconColor()}
                width={40}
                height={40}
                alt="Next Card"
              />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
}

export default VideoDialog;
